import { Fragment, type ReactNode } from 'react'
import { Link } from 'react-router'

/**
 * Markdown mínimo da resposta do Assistente (SPEC-20261104-2229): parágrafos,
 * listas, títulos, **negrito**, *itálico*, `código` e [links](/caminho).
 *
 * Monta elementos React, nunca HTML: o texto do modelo não tem como injetar
 * marcação. Link relativo (`/work-items/…`) navega dentro do Orbind; link
 * externo só se for http(s), em aba nova.
 */

type Bloco =
  | { tipo: 'p'; linhas: string[] }
  | { tipo: 'ul' | 'ol'; itens: string[] }
  | { tipo: 'h'; texto: string }

export function blocosDoMarkdown(texto: string): Bloco[] {
  const blocos: Bloco[] = []
  for (const linha of texto.replace(/\r\n/g, '\n').split('\n')) {
    const ul = /^\s*[-*•]\s+(.*)$/.exec(linha)
    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(linha)
    const h = /^\s*#{1,6}\s+(.*)$/.exec(linha)
    const ultimo = blocos.at(-1)
    if (ul || ol) {
      const tipo = ul ? 'ul' : 'ol'
      const item = (ul ?? ol)![1]!
      if (ultimo && ultimo.tipo === tipo) ultimo.itens.push(item)
      else blocos.push({ tipo, itens: [item] })
    } else if (h) {
      blocos.push({ tipo: 'h', texto: h[1]! })
    } else if (!linha.trim()) {
      blocos.push({ tipo: 'p', linhas: [] })
    } else if (ultimo && ultimo.tipo === 'p') {
      ultimo.linhas.push(linha)
    } else {
      blocos.push({ tipo: 'p', linhas: [linha] })
    }
  }
  return blocos.filter((b) => b.tipo !== 'p' || b.linhas.length > 0)
}

const EM_LINHA = /\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|`([^`]+)`|\*([^*\s][^*]*)\*/g

export function emLinha(texto: string): ReactNode[] {
  const partes: ReactNode[] = []
  let desde = 0
  for (const m of texto.matchAll(EM_LINHA)) {
    if (m.index > desde) partes.push(texto.slice(desde, m.index))
    const k = partes.length
    if (m[1] !== undefined && m[2] !== undefined) {
      const destino = m[2]
      if (destino.startsWith('/') && !destino.startsWith('//')) {
        partes.push(<Link key={k} to={destino}>{m[1]}</Link>)
      } else if (/^https?:\/\//i.test(destino)) {
        partes.push(<a key={k} href={destino} target="_blank" rel="noreferrer noopener">{m[1]}</a>)
      } else {
        partes.push(m[1])
      }
    } else if (m[3] !== undefined) partes.push(<strong key={k}>{m[3]}</strong>)
    else if (m[4] !== undefined) partes.push(<code key={k}>{m[4]}</code>)
    else if (m[5] !== undefined) partes.push(<em key={k}>{m[5]}</em>)
    desde = m.index + m[0].length
  }
  if (desde < texto.length) partes.push(texto.slice(desde))
  return partes
}

export function MarkdownDoAssistente({ texto }: { texto: string }) {
  return (
    <>
      {blocosDoMarkdown(texto).map((b, i) => {
        if (b.tipo === 'h') return <h4 key={i}>{emLinha(b.texto)}</h4>
        if (b.tipo === 'p') {
          return (
            <p key={i}>
              {b.linhas.map((l, j) => (
                <Fragment key={j}>
                  {j > 0 ? <br /> : null}
                  {emLinha(l)}
                </Fragment>
              ))}
            </p>
          )
        }
        const Lista = b.tipo
        return (
          <Lista key={i}>
            {b.itens.map((item, j) => (
              <li key={j}>{emLinha(item)}</li>
            ))}
          </Lista>
        )
      })}
    </>
  )
}
